import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  InventoryItem,
  InventoryItemInsert,
  InventoryItemUpdate,
  InventoryItemWithRelations,
} from './types';

const QUERY_KEY = ['inventory', 'items'] as const;

// ─────────────────────────────────────────────────────────────
// SELECT enrichi : item + catégorie + fournisseur + responsable
// ─────────────────────────────────────────────────────────────
const SELECT_WITH_RELATIONS = `
  *,
  category:inventory_categories(id, label, icon, color, profit_margin_percent),
  supplier:inventory_suppliers(id, name, is_saq),
  responsible:employees!responsible_employee_id(id, first_name, last_name)
` as const;

/**
 * Liste tous les produits avec leurs relations (catégorie, fournisseur,
 * responsable). Triés par nom.
 *
 * Pour filtrer par statut/catégorie/fournisseur, faire le filtre côté
 * client via .filter() ou un dérivé (memoized) — la liste reste raisonnable
 * (~600 items) pour ne pas multiplier les requêtes.
 */
export function useInventoryItems() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<InventoryItemWithRelations[]> => {
      // Pagination par pages de 1000 (limite dure PostgREST) jusqu'à
      // épuisement. Pour ~600 items actuels = UNE seule requête (600 < 1000) ;
      // ça ne tronque jamais quand les 407 produits legacy arriveront, même
      // si le projet Supabase plafonne db-max-rows à 1000 (où .limit(2000)
      // aurait silencieusement coupé à 1000).
      const PAGE = 1000;
      const all: InventoryItemWithRelations[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from('inventory_items')
          .select(SELECT_WITH_RELATIONS)
          .order('name', { ascending: true })
          .range(from, from + PAGE - 1);

        if (error) throw error;
        const rows = (data ?? []) as unknown as InventoryItemWithRelations[];
        all.push(...rows);
        if (rows.length < PAGE) break;
      }
      return all;
    },
  });
}

/**
 * Récupère un produit unique par id (avec relations). Utile pour
 * pré-remplir un formulaire d'édition sans re-télécharger la liste.
 */
export function useInventoryItem(id: string | null | undefined) {
  return useQuery({
    queryKey: ['inventory', 'item', id],
    enabled: !!id,
    queryFn: async (): Promise<InventoryItemWithRelations | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('inventory_items')
        .select(SELECT_WITH_RELATIONS)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return (data ?? null) as unknown as InventoryItemWithRelations | null;
    },
  });
}

/**
 * Crée un nouveau produit.
 *
 * Note : ne pas passer last_modified_at — il est NULL par défaut et
 * ne sera mis à jour qu'au premier changement de quantité.
 */
export function useCreateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: InventoryItemInsert): Promise<InventoryItem> => {
      const { data, error } = await supabase
        .from('inventory_items')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Met à jour un produit existant.
 *
 * Si le payload contient `quantity` (et qu'on n'a pas explicitement passé
 * last_modified_at), on tag automatiquement last_modified_at = now() pour
 * que la prochaine archive puisse détecter "modifié dans le cycle courant".
 *
 * Optimistic update : on patch tout de suite la cache locale pour que
 * l'UI réponde instantanément (sans attendre le round-trip Supabase).
 * En cas d'erreur, on rollback au snapshot précédent.
 */
export function useUpdateInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: InventoryItemUpdate;
    }): Promise<InventoryItem> => {
      const finalPayload: InventoryItemUpdate =
        payload.quantity !== undefined && payload.last_modified_at === undefined
          ? { ...payload, last_modified_at: new Date().toISOString() }
          : payload;

      const { data, error } = await supabase
        .from('inventory_items')
        .update(finalPayload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, payload }) => {
      // Annule les refetch en cours pour ne pas écraser la maj optimiste
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      const previous = queryClient.getQueryData<InventoryItemWithRelations[]>(QUERY_KEY);

      const stamp =
        payload.quantity !== undefined && payload.last_modified_at === undefined
          ? new Date().toISOString()
          : (payload.last_modified_at ?? null);

      // Si on change category_id ou responsible_employee_id, on doit aussi
      // patcher l'OBJET de relation correspondant (sinon la cellule continue
      // d'afficher l'ancien nom jusqu'au refetch ~500ms plus tard).
      const relationPatch: Partial<InventoryItemWithRelations> = {};

      if ('category_id' in payload) {
        const categories = queryClient.getQueryData<
          Array<{
            id: string;
            label: string;
            icon: string;
            color: string;
            profit_margin_percent: number | null;
          }>
        >(['inventory', 'categories']);
        const cat = categories?.find((c) => c.id === payload.category_id);
        relationPatch.category = cat
          ? {
              id: cat.id,
              label: cat.label,
              icon: cat.icon,
              color: cat.color,
              profit_margin_percent: cat.profit_margin_percent,
            }
          : null;
      }

      if ('responsible_employee_id' in payload) {
        const employees = queryClient.getQueryData<
          Array<{ id: string; first_name: string; last_name: string | null }>
        >(['employees']);
        const emp = employees?.find((e) => e.id === payload.responsible_employee_id);
        relationPatch.responsible = emp
          ? { id: emp.id, first_name: emp.first_name, last_name: emp.last_name }
          : null;
      }

      if ('supplier_id' in payload) {
        const suppliers = queryClient.getQueryData<
          Array<{ id: string; name: string; is_saq: boolean }>
        >(['inventory', 'suppliers']);
        const sup = suppliers?.find((s) => s.id === payload.supplier_id);
        relationPatch.supplier = sup
          ? { id: sup.id, name: sup.name, is_saq: sup.is_saq }
          : null;
      }

      queryClient.setQueryData<InventoryItemWithRelations[]>(QUERY_KEY, (old) => {
        if (!old) return old;
        return old.map((it) =>
          it.id === id
            ? {
                ...it,
                ...payload,
                ...relationPatch,
                ...(payload.quantity !== undefined ? { last_modified_at: stamp } : {}),
              }
            : it
        );
      });

      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Rollback si la mutation échoue côté serveur
      if (ctx?.previous) {
        queryClient.setQueryData(QUERY_KEY, ctx.previous);
      }
    },
    onSettled: (_data, _err, vars) => {
      // Re-sync avec le serveur une fois la mutation finie (success ou error)
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'item', vars.id] });
    },
  });
}

/**
 * Supprime un produit (hard delete).
 * Les lignes de commandes archivées le référençant auront item_id = NULL.
 */
export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Bulk update : met TOUS les last_modified_at à NULL.
 * Appelé après une "Sauvegarde d'inventaire" (Sprint 7.6) pour
 * commencer un nouveau cycle de modifications.
 */
export function useResetItemsModifiedFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      const { error } = await supabase
        .from('inventory_items')
        .update({ last_modified_at: null })
        .not('last_modified_at', 'is', null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
